<script lang="ts">
  import Layout from "$app/Layouts/Admin.svelte"
  import DataTable from "$app/components/DataTable.svelte"
  import { Trash } from "lucide-svelte"
  import { colorBoolean, formatDate, xhr, toast } from "$app/utils"
  import Autocomplete from "./Autocomplete.svelte"

  let props = $props()
  let {
    plan,
    subscription,
    users
  }: {
    plan: PlanType
    subscription: SubscriptionType
    users: UserType[]
  } = props
  let coaches: UserType[] = $state(props.coaches || [])

  async function addCoach(user: UserType): Promise<void> {
    try {
      const resp = await xhr.post(`/admin/plans/${plan.id}/add_coach`, {
        user_id: user.id
      })

      coaches = [...coaches, resp.data]
      toast("Coach added", "success")
    } catch (e) {
      console.error(e)
      toast("Coach could not be added", "error")
    } finally {
    }
  }

  async function removeCoach(coach: UserType) {
    try {
      xhr.delete(`/admin/plans/${plan.id}/remove_coach?user_id=${coach.id}`)

      toast("Coach deleted", "success")
      const idx = coaches.findIndex((c) => c.id === coach.id)
      coaches.splice(idx, 1)
      coaches = coaches
    } catch (e) {
      console.error(e)
      toast("Coach could not be deleted", "error")
    } finally {
    }
  }

  const fields = [
    { key: "id", label: "ID", sorting: false },
    { key: "role", label: "Role", sorting: false },
    {
      key: "name",
      label: "Name",
      sorting: false
    },
    {
      key: "email",
      label: "Email",
      sorting: false
    },
    {
      key: "enabled",
      label: "Enabled",
      format: (d) => {
        return `<span class="${colorBoolean(d.enabled)}">${
          d.enabled ? "EN" : "DIS"
        }ABLED</span>`
      }
    },
    {
      key: "created_at",
      label: "Created On",
      format: (d) => formatDate(d.created_at),
      sorting: false
    }
  ]

  //
  let coachFormStatus = ""
</script>

<Layout {...props}>
  <span slot="title">View Plan ID: {plan.id}</span>

  <div class="flex justify-start">
    <a
      href="/admin/plans"
      class="hover:text-blue-500 hover:underline text-gray-700 text-lg"
      >Back to Plans Index</a
    >
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <div class="flex gap-2 py-4 items-center">
      <div>
        <img src={plan.logo_url} alt={plan.name} class="w-12 h-12 mx-auto" />
        {#if plan.logo_url.match(/^data:image/)}
          <div class="text-gray-300 text-center">No Logo</div>
        {/if}
      </div>
      <div class="px-4">
        <span class="text-base text-gray-700">Plan Name:</span>
        <span class="text-lg font-bold" title="Name">{plan.name}</span>
      </div>
      <div class="px-4">
        <span class="text-base text-gray-700">Status:</span>
        <span
          class="text-sm"
          class:text-green-500={plan.enabled}
          class:text-red-500={!plan.enabled}
        >
          {plan.enabled ? "EN" : "DIS"}ABLED
        </span>
      </div>
    </div>

    <div>
      <div class="grid grid-cols-4 gap-2 h-16">
        <div>
          <span class="label-color">COMPANY NAME</span>
          {plan.company_name || ""}
        </div>
        <div>
          <span class="label-color">MARKET</span>
          {plan.market || ""}
        </div>

        <div>
          <span class="label-color">ZIP</span>
          {plan.zipcode || ""}
        </div>

        <div>
          <span class="label-color">ONBOARDING</span>
          <span
            class:text-green-500={plan.onboarding_complete}
            class:text-red-500={!plan.onboarding_complete}
          >
            {plan.onboarding_complete ? "COMPLETE" : "INCOMPLETE"}
          </span>
        </div>
      </div>

      <div class="grid grid-cols-4 gap-2 h-16">
        <div>
          <span class="label-color">TIME ZONE</span>
          {plan.time_zone || ""}
        </div>

        <div>
          <span class="label-color">MARKETING SOURCE</span>
          {plan.marketing_source || ""}
        </div>

        <div class="col-span-2">
          <span class="label-color">MARKETING SOURCE DETAIL</span>
          <span>
            {plan.marketing_source_detail || ""}
          </span>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <h2 class="text-xl font-bold mb-4">Users</h2>

    <DataTable {fields} data={users} />
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <div class="flex gap-2 items-center mb-4">
      <h2 class="text-xl font-bold">Coaches</h2>

      <div class="text-right">
        <div class="w-86 z-40">
          <Autocomplete
            data={users}
            {plan}
            onselected={(user) => {
              addCoach(user)
            }}
          />
        </div>
        <!--
        <button class="btn primary" on:click={() => (coachFormStatus = "new")}>
          New Coach
        </button>
        -->
      </div>
    </div>

    <DataTable {fields} data={coaches}>
      <svelte:fragment slot="actions" let:item>
        <div class="flex gap-4">
          <button onclick={() => removeCoach(item)}>
            <Trash />
          </button>
        </div>
      </svelte:fragment>
    </DataTable>
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <h2 class="text-xl font-bold mb-4">Subscription</h2>

    <div class="grid grid-cols-3 gap-2 h-16">
      <div>
        <span class="label-color">NAME</span>
        {subscription?.name || ""}
      </div>
      <div>
        <span class="label-color">STATUS</span>
        {subscription?.billing_status || ""}
      </div>
      <div>
        <span class="label-color">TRIAL DAYS</span>
        {subscription?.trial_days}
      </div>
    </div>

    <div class="grid grid-cols-3 gap-2 h-16">
      <div>
        <span class="label-color">TYPE</span>
        {subscription?.subscription_type || "N/A"}
      </div>
      <div>
        <span class="label-color">BILLING INTERVAL</span>
        {subscription?.billing_interval || ""}
      </div>
      <div>
        <span class="label-color"></span>
        {subscription?.trial_days}
      </div>
    </div>

    <div class="grid grid-cols-3 gap-2 h-16">
      <div>
        <span class="label-color">FULL-ACCESS TOTAL</span>
        {subscription?.full_access_total || "0"}
      </div>
      <div>
        <span class="label-color">TEAM MEMBER TOTAL</span>
        {subscription?.team_member_total || "0"}
      </div>
      <div>
        <span class="label-color">RECOGNITION MEMBER TOTAL</span>
        {subscription?.recognition_member_total || "0"}
      </div>
    </div>
  </div>
</Layout>
